import React, {
  useEffect,
  useState,
  useCallback,
  useRef,
  useMemo,
} from "react";
import TextField from "@mui/material/TextField";
import {
  Box,
  Paper,
  List,
  ListItem,
  ListItemText,
  Divider,
  IconButton,
  FormControlLabel,
} from "@mui/material";
import { Checkbox } from "cprime-foundational-components";
import { createPortal } from "react-dom";
import ClearIcon from "@mui/icons-material/Clear";
import CloseIcon from "@mui/icons-material/Close";
import Typography from "../../Components/Common/CprimeText";

interface JustificationInputProps {
  value?: string;
  onBlur: (
    data: any,
    value: string,
    fromElement: HTMLInputElement | null,
    toElement: HTMLElement | null,
    cursorPosition: number | null,
  ) => void;
  onChange?: any;
  row?: any;
  placeholder?: string;
  label?: string;
  required?: boolean;
  error?: boolean;
  previousJustifications?: string[]; // Array of previously used justifications
  isFirst?: boolean; // Whether this is the first justification input
  applyToAllChecked?: boolean; // Whether "apply to all" checkbox is checked
  onApplyToAllChange?: (checked: boolean) => void; // Callback for checkbox change
  firstJustificationValue?: string; // Value of the first justification input
  totalRows?: number; // Total number of rows
}

/**
 * JustificationInput Component
 * Handles input with local state management and focus restoration
 */
const JustificationInput: React.FC<JustificationInputProps> = ({
  value = "",
  onBlur,
  onChange,
  row,
  placeholder = "",
  label = "",
  required = true,
  error = false,
  previousJustifications = [],
  isFirst = false,
  applyToAllChecked = false,
  onApplyToAllChange,
  firstJustificationValue = "",
  totalRows = 0,
}) => {
  const [localValue, setLocalValue] = useState<string>(
    value || row?.inputtext || "",
  );
  const [isFocused, setIsFocused] = useState<boolean>(false);
  const [showSuggestions, setShowSuggestions] = useState<boolean>(false);
  const [panelPosition, setPanelPosition] = useState<{
    top: number;
    left: number;
    width: number;
  }>({
    top: 0,
    left: 0,
    width: 0,
  });
  const [customJustifications, setCustomJustifications] = useState<string[]>(
    [],
  );

  const inputRef = useRef<HTMLInputElement>(null);
  const suggestionPanelRef = useRef<HTMLDivElement>(null);
  const isUpdatingRef = useRef<boolean>(false); // Track if we're updating to prevent loops

  // Load custom justifications from localStorage on component mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem("customJustifications");
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed)) {
          setCustomJustifications(parsed);
        }
      }
    } catch (error) {
      // console.warn("Error loading custom justifications:", error);
    }
  }, []);

  // Save custom justifications to localStorage whenever they change
  useEffect(() => {
    if (customJustifications?.length > 0) {
      try {
        localStorage.setItem(
          "customJustifications",
          JSON.stringify(customJustifications),
        );
      } catch (error) {
        // console.warn("Error saving custom justifications:", error);
      }
    }
  }, [customJustifications]);

  // Sync local state with props only when not focused and value truly differs
  useEffect(() => {
    if (!isFocused && !isUpdatingRef.current) {
      const newValue = value || row?.inputtext || "";
      if (newValue !== localValue) {
        setLocalValue(newValue);
      }
    }
  }, [value, row?.inputtext, isFocused, localValue]);

  // Predefined justification suggestions
  const predefinedJustifications: string[] = [
    // "Business requirement for daily operations",
    // "Temporary access for project work",
    // "Role change requiring additional permissions",
    // "System integration testing purposes",
    // "Compliance and audit requirements",
    // "Training and development needs",
    // "Emergency access for critical issues",
    // "Process improvement initiative",
    // "Data analysis and reporting needs",
    // "Customer support requirements"
  ];

  // Combine all justifications (custom, previous, and predefined)
  const allJustifications = useMemo(() => {
    const combined = [
      ...customJustifications,
      ...(previousJustifications || []),
      ...(predefinedJustifications || []),
    ];

    // Remove duplicates while preserving order (custom first, then previous, then predefined)
    const unique = combined.filter(
      (item, index, arr) =>
        arr.findIndex(
          (i) => i.toLowerCase().trim() === item.toLowerCase().trim(),
        ) === index,
    );

    return unique;
  }, [customJustifications, previousJustifications]);

  /**
   * Add a new custom justification to the list
   */
  const addCustomJustification = useCallback(
    (justification: string) => {
      const trimmed = justification.trim();

      // Only add if it's not empty and doesn't already exist
      if (
        trimmed &&
        !customJustifications.some(
          (existing) => existing.toLowerCase() === trimmed.toLowerCase(),
        ) &&
        !predefinedJustifications.some(
          (existing) => existing.toLowerCase() === trimmed.toLowerCase(),
        ) &&
        !(previousJustifications || []).some(
          (existing) => existing.toLowerCase() === trimmed.toLowerCase(),
        )
      ) {
        setCustomJustifications((prev) => {
          const newList = [trimmed, ...prev].slice(0, 20);
          return newList;
        });
      } else {
        // console.log("Justification already exists or is empty:", trimmed);
      }
    },
    [customJustifications, previousJustifications, predefinedJustifications],
  );

  /**
   * Handles input value changes with immediate local state update
   */
  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      isUpdatingRef.current = true;
      setLocalValue(e.target.value);
      onChange?.(e?.target?.value, row?.feature_id);
      // Reset the flag after a short delay to allow parent updates
      setTimeout(() => {
        isUpdatingRef.current = false;
      }, 100);
    },
    [onChange, row?.feature_id],
  );

  /**
   * Simple positioning: just place panel below the input field
   */
  const updatePanelPosition = useCallback(() => {
    if (inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();

      setPanelPosition({
        top: rect.bottom + 4, // 4px below the input
        left: rect.left, // Align with input left edge
        width: Math.max(rect.width, 300), // At least 300px wide
      });
    }
  }, []);

  /**
   * Handles focus event to show suggestions
   */
  const handleFocus = useCallback(() => {
    setIsFocused(true);
    isUpdatingRef.current = false; // Reset flag on new focus
    updatePanelPosition();
    setShowSuggestions(true);
  }, [updatePanelPosition]);

  /**
   * Handles suggestion selection
   */
  const handleSuggestionClick = useCallback(
    (suggestion: string) => {
      isUpdatingRef.current = true;

      // Update local value
      setLocalValue(suggestion);

      // Hide suggestions immediately
      setShowSuggestions(false);

      // Trigger onChange for parent component
      onChange?.(suggestion, row?.feature_id);

      // Trigger onBlur to save state
      const mockFromElement = inputRef?.current;
      const mockToElement = null;
      const cursorPosition = suggestion?.length;

      onBlur(row, suggestion, mockFromElement, mockToElement, cursorPosition);

      // Keep focus on current input field
      setTimeout(() => {
        isUpdatingRef.current = false;

        if (inputRef.current) {
          const textField = inputRef?.current;
          textField?.focus();

          const inputElement = textField?.querySelector(
            "input",
          ) as HTMLInputElement;
          if (inputElement) {
            inputElement.focus();
            try {
              inputElement.setSelectionRange(
                suggestion?.length,
                suggestion?.length,
              );
            } catch (error) {
              // console.warn("Could not set cursor position:", error);
            }
          }
        }
      }, 10);
    },
    [onChange, row?.feature_id, onBlur],
  );

  /**
   * Handles blur event with focus target detection
   */
  const handleBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      const relatedTarget = e.relatedTarget as HTMLElement;
      if (
        relatedTarget &&
        suggestionPanelRef.current?.contains(relatedTarget)
      ) {
        return;
      }

      setIsFocused(false);
      setShowSuggestions(false);
      isUpdatingRef.current = false; // Reset flag on blur

      const fromElement = e.target;
      const cursorPosition = fromElement.selectionStart;

      // Always notify parent of value changes, even if empty
      setTimeout(() => {
        const toElement = document.activeElement as HTMLElement;
        onBlur(row, localValue, fromElement, toElement, cursorPosition);
      }, 50);

      // Only add to custom justifications if value is not empty
      if (localValue.trim() !== "") {
        addCustomJustification(localValue);
      }
    },
    [localValue, row, onBlur, addCustomJustification],
  );

  /**
   * Handle keydown events for better UX
   */
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Escape") {
        setShowSuggestions(false);
      } else if (e.key === "Enter") {
        if (showSuggestions && allJustifications?.length > 0) {
          e.preventDefault();
          handleSuggestionClick(allJustifications[0]);
        } else {
          // If no suggestions showing, just add the current value as custom
          const trimmed = localValue?.trim();
          if (trimmed) {
            addCustomJustification(trimmed);
          }
        }
      }
    },
    [
      showSuggestions,
      allJustifications,
      handleSuggestionClick,
      localValue,
      addCustomJustification,
    ],
  );

  /**
   * Handle click outside to close suggestions
   */
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      if (
        inputRef.current &&
        suggestionPanelRef.current &&
        !inputRef.current.contains(target) &&
        !suggestionPanelRef.current.contains(target)
      ) {
        setShowSuggestions(false);
      }
    };

    if (showSuggestions) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [showSuggestions]);

  /**
   * Clear all custom justifications - This will update localStorage immediately
   */
  const clearCustomJustifications = useCallback(() => {
    setCustomJustifications([]);

    // Force immediate localStorage update
    try {
      localStorage.setItem("customJustifications", JSON.stringify([]));

      // Dispatch a storage event to notify other instances
      window.dispatchEvent(
        new StorageEvent("storage", {
          key: "customJustifications",
          newValue: JSON.stringify([]),
          oldValue: localStorage.getItem("customJustifications"),
          storageArea: localStorage,
          url: window.location.href,
        }),
      );
    } catch (error) {
      // console.warn("Error clearing custom justifications:", error);
    }

    // Close the suggestion panel immediately
    setShowSuggestions(false);
  }, []);

  /**
   * Force re-render when localStorage changes from other instances
   */
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === "customJustifications") {
        try {
          const newValue = e.newValue || "[]";
          const parsed = JSON.parse(newValue);
          if (Array.isArray(parsed)) {
            setCustomJustifications(parsed);
          }
        } catch (error) {
          // console.warn("Error parsing storage change:", error);
        }
      }
    };

    // Listen for storage events from other tabs/instances
    window.addEventListener("storage", handleStorageChange);

    // Listen for custom storage events from same page instances
    window.addEventListener("storage", handleStorageChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-end",
      }}
      data-testid="box-outside"
    >
      {/* Debug info - remove in production */}
      {/* {process.env.NODE_ENV === 'development' && (
        <Box sx={{ fontSize: '0.75rem', color: 'gray', mb: 1 }}>
          Custom justifications count: {customJustifications.length}
          {customJustifications.length > 0 && (
            <div>Last: {customJustifications[0]}</div>
          )}
        </Box>
      )} */}

      <TextField
        ref={inputRef}
        value={localValue}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        label={label}
        required={required}
        error={localValue?.length === 0}
        variant="outlined"
        size="small"
        fullWidth
        inputProps={{
          "data-feature-id": row?.feature_id,
          autoComplete: "off",
          maxLength: 500,
        }}
        sx={{
          "& .MuiOutlinedInput-root": {
            marginTop: `${isFirst && totalRows > 1 ? 19 : 0}px`,
            fontSize: "14px",
            "& fieldset": {
              borderColor: "black",
            },
            "&.Mui-focused fieldset": {
              borderColor: "black",
              borderWidth: 1,
              fontSize: "14px",
            },
          },
        }}
      />

      {/* "Apply to all" checkbox - only show for first input when there are multiple rows */}
      {isFirst && totalRows > 1 && (
        <FormControlLabel
          control={
            <Checkbox
              checked={applyToAllChecked}
              onChange={(e) => onApplyToAllChange?.(e.target.checked)}
              disabled={!localValue || localValue?.trim()?.length === 0}
              size="small"
              sx={{
                padding: "4px",
                "& .MuiSvgIcon-root": {
                  fontSize: "16px",
                },
              }}
              style={{
                padding: "3px 8px 0 0",
                color:
                  !localValue || localValue?.trim()?.length === 0
                    ? "rgba(0, 0, 0, 0.38)"
                    : "rgba(0, 0, 0, 0.87)",
              }}
            />
          }
          label={
            <Typography
              sx={{
                fontSize: "14px",
                color:
                  !localValue || localValue?.trim()?.length === 0
                    ? "rgba(0, 0, 0, 0.38)"
                    : "rgba(0, 0, 0, 0.87)",
              }}
            >
              Apply to all
            </Typography>
          }
          sx={{
            margin: "0 3px 3px 0",
          }}
        />
      )}

      {showSuggestions &&
        createPortal(
          <Paper
            ref={suggestionPanelRef}
            sx={{
              position: "fixed",
              top: panelPosition?.top,
              left: panelPosition?.left,
              width: panelPosition?.width,
              zIndex: 9999,
              maxHeight: "250px",
              overflow: "auto",
              border:
                customJustifications?.length > 0 ? "1px solid #ccc" : "none",
              backgroundColor: "white",
              boxShadow: "0 8px 24px rgba(0,0,0,0.25)",
            }}
            elevation={24}
          >
            <List
              dense
              sx={{
                padding: 0,
                backgroundColor: "white",
                position: "relative",
                zIndex: 1,
              }}
            >
              {/* Custom justifications section */}
              {customJustifications?.length > 0 && (
                <>
                  <ListItem
                    sx={{
                      py: 0.5,
                      px: 2,
                      backgroundColor: "#fff",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography variant="caption" fontWeight="bold">
                      Justification Suggestions
                    </Typography>
                    <IconButton
                      size="small"
                      data-testid={`justification-suggestion`}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        clearCustomJustifications();
                      }}
                      sx={{ p: 0.5 }}
                      title="Clear all suggestions"
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </ListItem>
                  {customJustifications
                    .slice(0, 8)
                    .map((justification, index) => (
                      <ListItem
                        key={`custom-${index}`}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleSuggestionClick(justification);
                        }}
                        sx={{
                          py: 0.5,
                          px: 2,
                          "&:hover": {
                            backgroundColor: "#F8F8F9",
                            zIndex: 3,
                          },
                          cursor: "pointer",
                          minHeight: "32px",
                          backgroundColor: "white",
                          position: "relative",
                        }}
                      >
                        <ListItemText
                          primary={justification}
                          sx={{
                            "& .MuiListItemText-primary": {
                              fontSize: "14px",
                              fontWeight: "500",
                              // color: 'success.main',
                              wordBreak: "break-word",
                            },
                          }}
                        />
                      </ListItem>
                    ))}
                  {(previousJustifications?.length > 0 ||
                    predefinedJustifications?.length > 0) && (
                    <Divider sx={{ backgroundColor: "#ddd", height: 1 }} />
                  )}
                </>
              )}

              {/* Previous justifications */}
              {previousJustifications &&
                previousJustifications?.length > 0 &&
                previousJustifications
                  ?.slice(0, 5)
                  ?.map((justification, index) => (
                    <ListItem
                      key={`prev-${index}`}
                      data-testid={`prev-justification-${index}`}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleSuggestionClick(justification);
                      }}
                      sx={{
                        py: 0.5,
                        px: 2,
                        "&:hover": {
                          backgroundColor: "#F8F8F9",
                        },
                        cursor: "pointer",
                        minHeight: "32px",
                        backgroundColor: "white",
                      }}
                    >
                      <ListItemText
                        primary={justification}
                        sx={{
                          "& .MuiListItemText-primary": {
                            fontSize: "0.875rem",
                            wordBreak: "break-word",
                          },
                        }}
                      />
                    </ListItem>
                  ))}

              {/* Predefined justifications */}
              {predefinedJustifications.map((justification, index) => (
                <ListItem
                  key={`pred-${index}`}
                  data-testid={`predefined-justification-${index}`}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleSuggestionClick(justification);
                  }}
                  sx={{
                    py: 0.5,
                    px: 2,
                    "&:hover": {
                      backgroundColor: "#F8F8F9",
                    },
                    cursor: "pointer",
                    minHeight: "32px",
                    backgroundColor: "white",
                  }}
                >
                  <ListItemText
                    primary={justification}
                    sx={{
                      "& .MuiListItemText-primary": {
                        fontSize: "0.875rem",
                        wordBreak: "break-word",
                      },
                    }}
                  />
                </ListItem>
              ))}
            </List>
          </Paper>,
          document.body,
        )}
    </Box>
  );
};

export default JustificationInput;
